import { useEffect, useRef } from 'react';
import {
  Router,
  Switch,
  Route,
  Redirect,
  useLocation,
} from 'react-router-dom';
import TagManager from 'react-gtm-module';
import { PostHogProvider } from 'posthog-js/react';
import history from './history';
import './App.css';
import './scss/style.scss';
import { GoLockerDropOffStatusHost } from './components/trashie/global/GoLocker';
import { FirebaseProvider } from './components/global/FirebaseProvider/FirebaseProvider';
import AlertProvider from './components/providers/AlertProvider';

// Trashie
import {
  ROUTE_HOME,
  ROUTE_LOGIN,
  ROUTE_EMBED_LOGIN,
  ROUTE_LOGOUT,
  ROUTE_SIGNUP,
  ROUTE_DASHBOARD,
  ROUTE_REDEEM,
  ROUTE_ACCOUNT_SETTINGS,
  ROUTE_ACCOUNT_SETTINGS_DETAILS,
  ROUTE_ACCOUNT_SETTINGS_LOGIN,
  ROUTE_ACCOUNT_SETTINGS_PASSWORD,
  ROUTE_REGISTER,
  ROUTE_WALLET,
  ROUTE_TAKE_BACK_BAGS,
  ROUTE_TAKE_BACK_BAGS_DETAILS,
  ROUTE_TAKE_BACK_BAGS_BATCH,
  ROUTE_MY_ACCOUNT,
  ROUTE_ACCOUNT_GIFT_CARDS,
  ROUTE_ACCOUNT_GIFT_CARDS_HISTORY,
  ROUTE_ACCOUNT_GIFT_CARDS_ALL,
  ROUTE_ACCOUNT_GIFT_CARD_DETAIL,
  ROUTE_WEB_NOTIFICATIONS_INBOX,
  ROUTE_WEB_NOTIFICATIONS_INBOX_ACCOUNT,
  ROUTE_REWARD_ID,
  ROUTE_REWARD_INFO,
  ROUTE_DEAL_INFO,
  ROUTE_REWARDS_STORE_ID,
  ROUTE_REWARDS_STORE_REWARD_ID,
  ROUTE_REWARDS,
  ROUTE_TRANSACTIONS,
  ROUTE_OFFERS,
  ROUTE_REWARDS_OLD_STORE_ID,
  ROUTE_REWARDS_OLD_STORE_REWARD_ID,
  ROUTE_REWARD_OLD_INFO,
  ROUTE_DEALS,
  ROUTE_DEAL_STORE_ID,
  ROUTE_DEAL_STORE_REWARD_ID,
  ROUTE_GIFT_CARDS,
  ROUTE_GIFT_CARDS_BRAND_ID,
  ROUTE_DASHBOARD_REWARDS,
  ROUTE_IMPACT,
  ROUTE_EMBED_BROWN_FORM,
  ROUTE_REWARDS_V2,
  ROUTE_REWARDS_V2_LIST,
  ROUTE_REWARDS_V2_STORE_ID,
  ROUTE_REWARDS_V2_STORE_REWARD_ID,
  ROUTE_ARTICLES,
  ROUTE_ARTICLE_DETAIL,
  ROUTE_ARTICLE_DETAIL_PRODUCTS,
  ROUTE_GIFT_CARDS_V2,
  ROUTE_GIFT_CARDS_V2_BRAND_ID,
  ROUTE_OFFERS_V2,
  ROUTE_MANAGE_MEMBERSHIP,
  ROUTE_SUBSCRIPTION,
  ROUTE_UNLIMITED,
  ROUTE_TRASHIE_UNLIMITED,
  ROUTE_MEMBERSHIP_ORDERS,
  ROUTE_MEMBERSHIP_PAYMENT_METHOD,
  ROUTE_MEMBERSHIP_PAYMENT_METHOD_EMAIL,
  ROUTE_MEMBERSHIP,
  ROUTE_MEMBERSHIP_CANCEL,
  ROUTE_MOBILE_LOGIN,
  ROUTE_MOBILE_VERIFY_EMAIL,
  ROUTE_WELCOME_BACK,
  ROUTE_MOBILE_MEMBERSHIP_SUCCESS,
} from './components/trashie/config/routes';
import {
  Container,
  TrashieContainer,
  TrashieContainerPublic,
  TrashieContainerPublicCentered,
  TrashieMembershipContainer,
} from './components/trashie/containers';
import TrashiePublicRoute from './components/trashie/global/Routes/PublicRoute';
import TrashiePrivateRoute from './components/trashie/global/Routes/PrivateRoute';
import HomeRedirect from './components/trashie/global/Routes/HomeRedirect';
import EnvironmentBanner from './components/trashie/global/EnvironmentBanner';

import { isProd } from './config/config';
import useScript from './hooks/useScript';
import useExternalScript from './hooks/useExternalScript';
import gaScript from './components/trashie/scripts/ga';
import equalwebScript from './components/trashie/scripts/equalweb';
import redditPixelScript from './components/trashie/scripts/redditPixel';
import tiktokPixelScript from './components/trashie/scripts/tiktokPixel';
import magellanPixelScript, { trackMagellanPageView } from './components/trashie/scripts/magellanPixel';

import {
  TRASHIE_POSTHOG_API_HOST,
  TRASHIE_POSTHOG_API_KEY,
  TRASHIE_GA_CONTAINER_ID,
} from './components/trashie/config/config';
import { GOOGLE_TAG_MANAGER_URL } from './components/trashie/config/externalURL';
import { setDocumentTitle } from './components/trashie/utils/utils';

import MaintenanceBanner from './components/trashie/global/MaintenanceBanner';
import AnalyticsInitializer from './components/trashie/global/AnalyticsInitializer';
import { createBeforeSendHook } from './components/trashie/utils/analytics';
import UtmProvider from './components/trashie/providers/UtmProvider';
import lazyWithRetry from './utils/lazyWithRetry';
import { STEP_UP_RECAPTCHA_CONTAINER_ID } from './utils/firebase';

// Route-level code splitting: these route views load on demand as separate chunks.
// The Suspense boundary + <Loading/> fallback lives in TrashiePublicRoute/TrashiePrivateRoute.
// lazyWithRetry retries failed chunk fetches and falls back to one guarded reload
// (LogRocket: prod chunk-load timeout left /articles permanently blank, 2026-07-08).
const RedemptionLandingPage = lazyWithRetry(() => import('./components/trashie/views/Register'));
const Subscription = lazyWithRetry(() => import('./components/trashie/views/Subscription').then((m) => ({ default: m.Subscription })));
const TrashieLogin = lazyWithRetry(() => import('./components/trashie/views/Login'));
const TrashieEmbedLogin = lazyWithRetry(() => import('./components/trashie/views/Embed/Login'));
const TrashieLogout = lazyWithRetry(() => import('./components/trashie/views/Logout'));
const TrashieDashboardRewards = lazyWithRetry(() => import('./components/trashie/views/RewardsV3'));
const TrashieImpact = lazyWithRetry(() => import('./components/trashie/views/Impact/Impact'));
const TrashieMyAccount = lazyWithRetry(() => import('./components/trashie/views/MyAccount'));
const NotificationsInboxWebRedirect = lazyWithRetry(() => import('./components/trashie/views/NotificationsInboxWebRedirect/NotificationsInboxWebRedirect'));
const TrashieGiftCard = lazyWithRetry(() => import('./components/trashie/views/GiftCardBrand'));
const Articles = lazyWithRetry(() => import('./components/trashie/views/Articles'));
const ArticleDetail = lazyWithRetry(() => import('./components/trashie/views/ArticleDetail'));
const ArticleProducts = lazyWithRetry(() => import('./components/trashie/views/ArticleProducts'));
const TrashieMembershipOrders = lazyWithRetry(() => import('./components/trashie/views/MembershipOrders'));
const MembershipPaymentMethod = lazyWithRetry(() => import('./components/trashie/views/MembershipPaymentMethod'));
const MembershipPaymentMethodEmail = lazyWithRetry(() => import('./components/trashie/views/MembershipPaymentMethodEmail'));
const TrashieMembership = lazyWithRetry(() => import('./components/trashie/views/MembershipDashboard'));
const TrashieMembershipCancel = lazyWithRetry(() => import('./components/trashie/views/CancelSubscription'));
const BrownForm = lazyWithRetry(() => import('./components/trashie/views/Embed/BrownForm'));
const TrashieManageMembership = lazyWithRetry(() => import('./components/trashie/views/ManageMembership'));
const MobileRedirect = lazyWithRetry(() => import('./components/trashie/views/MobileRedirect'));

const isFirstPartyEmbed = (() => {
  try {
    return window.self !== window.top
      && document.referrer
      && /(?:^|\.)trashie\.io$/.test(new URL(document.referrer).hostname);
  } catch (error) {
    return false;
  }
})();

if (window.self === window.top || isFirstPartyEmbed) {
  TagManager.initialize({
    gtmId: TRASHIE_GA_CONTAINER_ID,
  });
}

const MagellanRouteTracker = () => {
  const location = useLocation();
  const isFirstRouteRender = useRef(true);

  useEffect(() => {
    if (isFirstRouteRender.current) {
      isFirstRouteRender.current = false;
      return;
    }

    trackMagellanPageView();
  }, [location.pathname, location.search]);

  return null;
};

const AccountGiftCardsRouteBridge = () => null;
const AccountSettingsRouteBridge = () => null;
const TransactionsRouteBridge = () => null;
// Take Back Bags now opens as an account-drawer panel; these URLs are bridges
// that TrashieContainer intercepts to open the drawer (then history.replaces back).
const TakeBackBagsRouteBridge = () => null;

const App = () => {
  useExternalScript(GOOGLE_TAG_MANAGER_URL);
  useScript(gaScript);
  useScript(equalwebScript);
  useScript(redditPixelScript);
  // Google Maps/Places is loaded on demand by the address forms that need it
  // (see useGoogleMapsPlaces), not on every page.
  useScript(tiktokPixelScript);
  useScript(magellanPixelScript);

  useEffect(() => {
    setDocumentTitle();
  }, []);

  return (
    <FirebaseProvider>
      <AlertProvider>
        <UtmProvider>
          <Router history={history}>
            <Switch>
              <PostHogProvider
                apiKey={isProd() ? (
                  TRASHIE_POSTHOG_API_KEY.PRODUCTION
                ) : (
                  TRASHIE_POSTHOG_API_KEY.STAGING
                )}
                options={{
                  api_host: TRASHIE_POSTHOG_API_HOST,
                  person_profiles: 'always',
                  cross_subdomain_cookie: true,
                  persistence: 'localStorage+cookie',
                  before_send: createBeforeSendHook(),
                }}
              >
                <MagellanRouteTracker />
                <AnalyticsInitializer>
                  {!isProd() && <EnvironmentBanner />}
                  <MaintenanceBanner />
                  <Container>
                    <Route exact path={ROUTE_HOME}>
                      <HomeRedirect />
                    </Route>
                    <Route exact path="/profile">
                      <Redirect to={ROUTE_ACCOUNT_SETTINGS} />
                    </Route>
                    <TrashiePublicRoute
                      path={ROUTE_REDEEM}
                      exact
                      render={(location) => (
                        <Redirect to={`${ROUTE_REGISTER}${location.search}`} />
                      )}
                    />
                    <TrashiePublicRoute
                      path={ROUTE_SIGNUP}
                      exact
                      render={(location) => (
                        <Redirect to={`${ROUTE_LOGIN}${location.search}`} />
                      )}
                    />
                    <TrashiePublicRoute
                      path={ROUTE_REGISTER}
                      exact
                      handleRedirect={false}
                      component={RedemptionLandingPage}
                    />
                    <TrashiePublicRoute
                      path={ROUTE_SUBSCRIPTION}
                      exact
                      render={(location) => (
                        <Redirect
                          to={{
                            pathname: ROUTE_UNLIMITED,
                            search: location.search,
                            hash: location.hash,
                            ...(location.state != null ? { state: location.state } : {}),
                          }}
                        />
                      )}
                    />
                    <TrashiePublicRoute
                      path={ROUTE_UNLIMITED}
                      exact
                      handleRedirect={false}
                      component={Subscription}
                    />
                    <TrashiePublicRoute
                      path={ROUTE_TRASHIE_UNLIMITED}
                      exact
                      handleRedirect={false}
                      component={Subscription}
                    />
                    <TrashiePublicRoute
                      path={ROUTE_MOBILE_LOGIN}
                      exact
                      handleRedirect={false}
                      component={MobileRedirect}
                    />
                    <TrashiePublicRoute
                      path={ROUTE_MOBILE_VERIFY_EMAIL}
                      exact
                      handleRedirect={false}
                      component={MobileRedirect}
                    />
                    <TrashiePublicRoute
                      path={ROUTE_MOBILE_MEMBERSHIP_SUCCESS}
                      exact
                      handleRedirect={false}
                      component={MobileRedirect}
                    />
                    <TrashiePublicRoute
                      path={ROUTE_WELCOME_BACK}
                      exact
                      handleRedirect={false}
                      component={MobileRedirect}
                    />
                  </Container>
                  <TrashieContainerPublicCentered>
                    <TrashiePublicRoute
                      path={ROUTE_LOGIN}
                      exact
                      handleRedirect={false}
                      component={TrashieLogin}
                    />
                  </TrashieContainerPublicCentered>
                  <TrashieContainerPublic>
                    <TrashiePublicRoute
                      path={ROUTE_EMBED_LOGIN}
                      exact
                      handleRedirect={false}
                      component={TrashieEmbedLogin}
                    />
                    <TrashiePublicRoute
                      path={ROUTE_EMBED_BROWN_FORM}
                      exact
                      handleRedirect={false}
                      component={BrownForm}
                    />
                  </TrashieContainerPublic>
                  <TrashieContainer>
                    <TrashiePrivateRoute
                      path={ROUTE_LOGOUT}
                      exact
                      component={TrashieLogout}
                    />
                    <TrashiePublicRoute
                      path={ROUTE_ARTICLE_DETAIL_PRODUCTS}
                      exact
                      handleRedirect={false}
                      component={ArticleProducts}
                    />
                    <TrashiePublicRoute
                      path={ROUTE_ARTICLE_DETAIL}
                      exact
                      handleRedirect={false}
                      component={ArticleDetail}
                    />
                    <TrashiePublicRoute
                      path={ROUTE_ARTICLES}
                      exact
                      handleRedirect={false}
                      component={Articles}
                    />
                    <Route exact path={ROUTE_REWARDS_V2_STORE_ID}>
                      <Redirect to={ROUTE_ARTICLES} />
                    </Route>
                    <Route exact path={ROUTE_REWARDS_V2_STORE_REWARD_ID}>
                      <Redirect to={ROUTE_ARTICLES} />
                    </Route>
                    <Route exact path={ROUTE_REWARDS_V2_LIST}>
                      <Redirect to={ROUTE_ARTICLES} />
                    </Route>
                    <Route exact path={ROUTE_REWARDS_V2}>
                      <Redirect to={ROUTE_ARTICLES} />
                    </Route>
                    <TrashiePrivateRoute
                      path={ROUTE_GIFT_CARDS_V2_BRAND_ID}
                      exact
                      component={TrashieGiftCard}
                    />
                    <Route exact path={ROUTE_GIFT_CARDS_V2}>
                      <Redirect to={ROUTE_DASHBOARD_REWARDS} />
                    </Route>
                    <Route exact path={`${ROUTE_GIFT_CARDS_V2}/list`}>
                      <Redirect to={ROUTE_DASHBOARD_REWARDS} />
                    </Route>
                    <Route exact path={ROUTE_OFFERS_V2}>
                      <Redirect to={ROUTE_ARTICLES} />
                    </Route>
                    <Route exact path={ROUTE_DASHBOARD}>
                      <Redirect to={ROUTE_DASHBOARD_REWARDS} />
                    </Route>
                    <TrashiePrivateRoute
                      path={ROUTE_DASHBOARD_REWARDS}
                      exact
                      component={TrashieDashboardRewards}
                    />
                    <TrashiePrivateRoute path={ROUTE_IMPACT} exact component={TrashieImpact} />
                    <Route exact path={ROUTE_WALLET}>
                      <Redirect to={ROUTE_ARTICLES} />
                    </Route>
                    <TrashiePrivateRoute
                      path={ROUTE_TAKE_BACK_BAGS_DETAILS}
                      exact
                      component={TakeBackBagsRouteBridge}
                    />
                    <TrashiePrivateRoute
                      path={ROUTE_TAKE_BACK_BAGS_BATCH}
                      exact
                      component={TakeBackBagsRouteBridge}
                    />
                    <TrashiePrivateRoute
                      path={ROUTE_TAKE_BACK_BAGS}
                      exact
                      component={TakeBackBagsRouteBridge}
                    />
                    <TrashiePrivateRoute
                      path={ROUTE_MY_ACCOUNT}
                      exact
                      component={TrashieMyAccount}
                    />
                    <TrashiePrivateRoute
                      path={ROUTE_ACCOUNT_SETTINGS_PASSWORD}
                      exact
                      component={AccountSettingsRouteBridge}
                    />
                    <TrashiePrivateRoute
                      path={ROUTE_ACCOUNT_SETTINGS_LOGIN}
                      exact
                      component={AccountSettingsRouteBridge}
                    />
                    <TrashiePrivateRoute
                      path={ROUTE_ACCOUNT_SETTINGS_DETAILS}
                      exact
                      component={AccountSettingsRouteBridge}
                    />
                    <TrashiePrivateRoute
                      path={ROUTE_ACCOUNT_SETTINGS}
                      exact
                      component={AccountSettingsRouteBridge}
                    />
                    <TrashiePrivateRoute
                      path={ROUTE_ACCOUNT_GIFT_CARDS_HISTORY}
                      exact
                      component={AccountGiftCardsRouteBridge}
                    />
                    <TrashiePrivateRoute
                      path={ROUTE_ACCOUNT_GIFT_CARDS_ALL}
                      exact
                      component={AccountGiftCardsRouteBridge}
                    />
                    <TrashiePrivateRoute
                      path={ROUTE_ACCOUNT_GIFT_CARD_DETAIL}
                      exact
                      component={AccountGiftCardsRouteBridge}
                    />
                    <TrashiePrivateRoute
                      path={ROUTE_ACCOUNT_GIFT_CARDS}
                      exact
                      component={AccountGiftCardsRouteBridge}
                    />
                    <TrashiePrivateRoute
                      path={ROUTE_WEB_NOTIFICATIONS_INBOX}
                      exact
                      component={NotificationsInboxWebRedirect}
                    />
                    <TrashiePrivateRoute
                      path={ROUTE_WEB_NOTIFICATIONS_INBOX_ACCOUNT}
                      exact
                      component={NotificationsInboxWebRedirect}
                    />
                    <Route exact path={ROUTE_REWARD_ID}>
                      <Redirect to={ROUTE_DASHBOARD_REWARDS} />
                    </Route>
                    <Route exact path={ROUTE_REWARDS_OLD_STORE_ID}>
                      <Redirect to={ROUTE_DASHBOARD_REWARDS} />
                    </Route>
                    <Route exact path={ROUTE_REWARDS_OLD_STORE_REWARD_ID}>
                      <Redirect to={ROUTE_DASHBOARD_REWARDS} />
                    </Route>
                    <Route exact path={ROUTE_REWARD_OLD_INFO}>
                      <Redirect to={ROUTE_DASHBOARD_REWARDS} />
                    </Route>
                    <Route exact path={ROUTE_REWARDS_STORE_ID}>
                      <Redirect to={ROUTE_DASHBOARD_REWARDS} />
                    </Route>
                    <Route exact path={ROUTE_REWARDS_STORE_REWARD_ID}>
                      <Redirect to={ROUTE_DASHBOARD_REWARDS} />
                    </Route>
                    <Route exact path={ROUTE_REWARD_INFO}>
                      <Redirect to={ROUTE_DASHBOARD_REWARDS} />
                    </Route>
                    <Route exact path={ROUTE_DEAL_INFO}>
                      <Redirect to={ROUTE_ARTICLES} />
                    </Route>
                    <Route exact path={ROUTE_REWARDS}>
                      <Redirect to={ROUTE_DASHBOARD_REWARDS} />
                    </Route>
                    <TrashiePrivateRoute
                      path={ROUTE_TRANSACTIONS}
                      exact
                      component={TransactionsRouteBridge}
                    />
                    <Route exact path={ROUTE_OFFERS}>
                      <Redirect to={ROUTE_ARTICLES} />
                    </Route>
                    <Route exact path={ROUTE_DEALS}>
                      <Redirect to={ROUTE_ARTICLES} />
                    </Route>
                    <Route exact path={ROUTE_DEAL_STORE_ID}>
                      <Redirect to={ROUTE_ARTICLES} />
                    </Route>
                    <Route exact path={ROUTE_DEAL_STORE_REWARD_ID}>
                      <Redirect to={ROUTE_ARTICLES} />
                    </Route>
                    <Route exact path={ROUTE_GIFT_CARDS}>
                      <Redirect to={ROUTE_DASHBOARD_REWARDS} />
                    </Route>
                    <Route
                      exact
                      path={ROUTE_GIFT_CARDS_BRAND_ID}
                      render={({ match }) => (
                        <Redirect to={match.url.replace(ROUTE_GIFT_CARDS, ROUTE_GIFT_CARDS_V2)} />
                      )}
                    />
                    <TrashieMembershipContainer>
                      <TrashiePrivateRoute
                        path={ROUTE_MEMBERSHIP}
                        exact
                        component={TrashieMembership}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_MANAGE_MEMBERSHIP}
                        exact
                        component={TrashieManageMembership}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_MEMBERSHIP_ORDERS}
                        exact
                        component={TrashieMembershipOrders}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_MEMBERSHIP_PAYMENT_METHOD_EMAIL}
                        exact
                        component={MembershipPaymentMethodEmail}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_MEMBERSHIP_PAYMENT_METHOD}
                        exact
                        component={MembershipPaymentMethod}
                      />
                      <TrashiePrivateRoute
                        path={ROUTE_MEMBERSHIP_CANCEL}
                        exact
                        component={TrashieMembershipCancel}
                      />
                    </TrashieMembershipContainer>
                  </TrashieContainer>
                </AnalyticsInitializer>
              </PostHogProvider>
            </Switch>
            {/* GoLocker drop-off status (G5/G6), opened from anywhere through
                openGoLockerDropOffStatus({ code | batchId }). */}
            <GoLockerDropOffStatusHost />
          </Router>
        </UtmProvider>
      </AlertProvider>
      <div id="recaptcha-container" />
      <div id={STEP_UP_RECAPTCHA_CONTAINER_ID} />
    </FirebaseProvider>
  );
};

export default App;
